/* global grecaptcha */

import { Controller } from 'stimulus';
import porta from 'porta';
import nl from 'porta/src/i18n/nl';
import toArray from '../helpers/toArray';

class FormValidationController extends Controller {
  connect() {
    porta({
      target: this.element,
      messages: nl,
      onValidate: (formElement) => {
        this.disableSubmitButton(formElement);
      },
      onError: (formElement) => {
        this.enableSubmitButton(formElement);
      },
      onValid: (formElement) => {
        this.enableSubmitButton(formElement);

        const event = document.createEvent('CustomEvent');
        event.initCustomEvent('valid', true, true);
        formElement.dispatchEvent(event);

        const recaptcha = formElement.querySelector('.js-g-recaptcha');

        if (recaptcha) {
          grecaptcha.execute(recaptcha.getAttribute('data-widget-id'));
          return false;
        }

        return true;
      },
    });

    if (this.data.get('realtime-feedback') !== null) {
      this.setupRealtimeFeedback();
    }
  }

  disableSubmitButton(formElement) {
    this.toggleSubmitButtonState(formElement, false);
  }

  enableSubmitButton(formElement) {
    this.toggleSubmitButtonState(formElement, true);
  }

  // eslint-disable-next-line
  toggleSubmitButtonState(formElement, state) {
    toArray(formElement.querySelectorAll('[type="submit"]')).forEach((button) => {
      button[state ? 'removeAttribute' : 'setAttribute']('disabled', 'disabled');
    });
  }

  setupRealtimeFeedback() {
    const inputFieldSelectors = [
      'input[type="text"]',
      'input[type="number"]',
      'input[type="tel"]',
      'input[type="email"]',
      'textarea',
      'select',
    ];

    const inputFields = toArray(this.element.querySelectorAll(inputFieldSelectors.join(', ')));

    const validateAndOutput = (inputField) => {
      this.outputValidationStateRealtime(inputField, this.validateFieldRealtime(inputField));
    };

    inputFields.forEach((inputField) => {
      if (inputField.type === 'checkbox' || inputField.type === 'radio') {
        // Do nothing for now.
        return;
      }

      if (inputField.tagName === 'SELECT') {
        inputField.addEventListener('change', () => {
          validateAndOutput(inputField);
        });

        return;
      }

      inputField.addEventListener('blur', () => {
        inputField.classList.add('is-blurred');
        validateAndOutput(inputField);
      });

      inputField.addEventListener('keyup', () => {
        if (inputField.classList.contains('is-blurred')) {
          validateAndOutput(inputField);
        }
      });
    });
  }

  /* eslint-disable-next-line class-methods-use-this */
  outputValidationStateRealtime(inputField, state) {
    inputField.parentNode.classList[state ? 'add' : 'remove']('is-success');
    inputField.parentNode.classList[state ? 'remove' : 'add']('is-wrong');
  }

  /* eslint-disable-next-line class-methods-use-this */
  validateFieldRealtime(inputField) {
    if (inputField.getAttribute('required') !== null) {
      if (!inputField.value) {
        return false;
      }
    }

    if (inputField.getAttribute('minlength') !== null) {
      const minLength = parseInt(inputField.getAttribute('minlength'), 10);

      if (inputField.value.length < minLength) {
        return false;
      }
    }

    if (inputField.getAttribute('maxlength') !== null) {
      const maxLength = parseInt(inputField.getAttribute('maxlength'), 10);

      if (inputField.value.length > maxLength) {
        return false;
      }
    }

    if (inputField.getAttribute('type') === 'email') {
      // Beware that this is a really basic email check. Further checking is done
      // on the server.
      const mailRegex = /\S+@\S+\.\S+/;

      if (!mailRegex.test(inputField.value)) {
        return false;
      }
    }

    if (inputField.getAttribute('type') === 'tel') {
      // Basic regex to make sure no letters are in the value.
      const telRegex = /^[\d\s-()+.]+$/;

      if (!telRegex.test(inputField.value)) {
        return false;
      }
    }

    return true;
  }
}

export default FormValidationController;
